import type {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  FilterOptionsState,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import React, { Component, RefObject, SyntheticEvent } from 'react';
import AddIcon from '../../resources/images/icons-add.svg';
import DropIcon from '../../resources/images/icons-chevron-double.svg';
import RemoveIcon from '../../resources/images/icons-remove.svg';

import type {
  AutocompleteRenderOptionState,
  BaseSelectorOption,
  MultiSelectorProps,
  MultiSelectorState,
} from '../../types/components/selector.types';
import IntegrationAbbreviation from '../../views/settings/IntegrationAbbreviation';
import { TextField } from '../TextField';
import { MultiSelectorContent } from './MultiSelectorContent';

export class MultiSelector<
  TOption extends BaseSelectorOption = BaseSelectorOption,
> extends Component<MultiSelectorProps<TOption>, MultiSelectorState<TOption>> {
  static defaultProps: Partial<MultiSelectorProps<BaseSelectorOption>> = {
    allowMultiple: true,
    newItemLabel: undefined,
    hasAllItem: false,
    allSelected: false,
  };

  filterOptions: (options: TOption[], state: FilterOptionsState<TOption>) => TOption[];
  selectorContainerRef: RefObject<HTMLDivElement | null>;
  debounceSearchTimer: ReturnType<typeof setTimeout> | null;

  constructor(props: MultiSelectorProps<TOption>) {
    super(props);

    this.state = {
      selectedValues: props.values ? props.values : [],
      allSelected: props.allSelected ?? false,
      valid: null,
      inputValue: '',
      hasAllItem: props.hasAllItem ?? false,
      allItemLabel: props.allItemLabel || props.translate?.('common.all') || 'All',
      selectorContainerSize: null,
    };

    this.filterOptions = createFilterOptions({
      matchFrom: 'any',
      stringify: (option: TOption) => this.getItemLabel(option),
    });

    this.selectorContainerRef = React.createRef();
    this.debounceSearchTimer = null;
  }

  componentDidMount(): void {
    const { isValidateOnMount } = this.props;

    this.onGetSelectorContainerSize();

    if (isValidateOnMount) {
      this.validate(null);
    }
  }

  componentDidUpdate(
    prevProps: MultiSelectorProps<TOption>,
    _prevState: MultiSelectorState<TOption>
  ): void {
    if (prevProps.values !== this.props.values) {
      this.setState({ selectedValues: this.props.values || [] });
      this.validate(null);
    }
    if (prevProps.allSelected !== this.props.allSelected)
      this.setState({ allSelected: this.props.allSelected ?? false });
  }

  onGetSelectorContainerSize(): void {
    const selectorContainerSize =
      this.selectorContainerRef?.current?.getBoundingClientRect() ?? null;
    this.setState({ selectorContainerSize });
  }

  onChange(
    e: SyntheticEvent,
    selected: TOption | TOption[] | null,
    reason?: AutocompleteChangeReason
  ): void {
    const { onChange } = this.props;
    const { selectedValues } = this.state;

    if (Array.isArray(selected) && selected.some((i) => i.isNewItem)) {
      this.onNewItemClick(e);
      return;
    }

    if (reason === 'removeOption' && e?.type !== 'click') return;

    const singleSelected = selected as TOption;
    this.setState(
      {
        inputValue: '',
        allSelected: singleSelected.isAllItem ?? false,
        selectedValues: Array.isArray(selected)
          ? selected
          : singleSelected.isAllItem
            ? [singleSelected]
            : [...selectedValues.filter((i) => !i.isAllItem), singleSelected].filter(
                (v, i, a) => a.indexOf(v) === i
              ),
      },
      () => {
        if (onChange) onChange(e, this.state.selectedValues, singleSelected.isAllItem);
      }
    );
  }

  onInputChange(
    e: SyntheticEvent | null,
    value: string,
    reason: AutocompleteInputChangeReason
  ): void {
    const { onSearch } = this.props;

    if (e && onSearch && reason === 'input') {
      const target = e.target as HTMLInputElement;
      onSearch(target.value);
    }
    if (e) {
      const target = e.target as HTMLInputElement;
      this.setState({ inputValue: target.value });
    }
  }

  addItem(newItem: TOption): void {
    const { onChange } = this.props;
    const { selectedValues } = this.state;

    this.setState({ selectedValues: [...selectedValues, newItem] }, () => {
      if (onChange) onChange(null, this.state.selectedValues);
    });
  }

  onNewItemClick(e: SyntheticEvent): void {
    const { onNewItemClick } = this.props;
    const { inputValue } = this.state;

    if (onNewItemClick)
      onNewItemClick(e, inputValue, (newItem: TOption | null) => {
        if (newItem) this.addItem(newItem);
      });
  }

  onBlur(e: React.FocusEvent<HTMLElement>): void {
    const { onBlur } = this.props;

    this.validate(e);
    this.setState({ inputValue: '' });

    if (onBlur) onBlur(e);
  }

  validate(e: SyntheticEvent | null): void {
    const { validationMethod } = this.props;
    const { valid } = this.state;

    if (validationMethod) {
      const target = e?.target as HTMLInputElement | null;
      const newValid = validationMethod(e, target?.value ?? null);
      if (newValid !== valid) this.setState({ valid: newValid });
    }
  }

  renderNewItemOption(props: React.HTMLAttributes<HTMLLIElement>): React.ReactNode {
    const { newItemLabel, translate } = this.props;
    const { inputValue } = this.state;
    const createLabel = translate ? translate('common.create') : 'Create';

    return (
      <ListItem
        {...props}
        className='klayo-multiselector_additem'
        onClick={this.onNewItemClick.bind(this)}
      >
        <ListItemIcon>
          <AddIcon />
        </ListItemIcon>
        <ListItemText>
          {newItemLabel ? newItemLabel : createLabel + ' ' + (inputValue ? inputValue : '')}
        </ListItemText>
      </ListItem>
    );
  }

  renderOption(
    props: React.HTMLAttributes<HTMLLIElement>,
    option: TOption,
    state: AutocompleteRenderOptionState
  ): React.ReactNode {
    const { hasNewItem, getItemDescription } = this.props;
    const { selectorContainerSize } = this.state;

    if (hasNewItem && option.isNewItem) return this.renderNewItemOption(props);

    return (
      <MultiSelectorContent
        option={option}
        description={getItemDescription?.(option)}
        inputValue={state.inputValue}
        props={props}
        selectorContainerSize={selectorContainerSize}
        getItemLabel={this.getItemLabel.bind(this)}
        getItemId={this.getItemId.bind(this)}
        key={option.id}
      />
    );
  }

  getItemLabel(item: TOption): string {
    const { hasNoneItem, noneItemLabel, hasAllItem, allItemLabel, hasNewItem, getItemLabel } =
      this.props;
    const { inputValue } = this.state;

    if (hasNoneItem && item.isNoneItem === true) return noneItemLabel || 'None';

    if (hasAllItem && item.isAllItem === true) return allItemLabel || 'All';

    if (hasNewItem && item.isNewItem === true) return inputValue;

    return item ? (getItemLabel ? getItemLabel(item) : (item.name ?? '')) : '';
  }

  getItemId(item: TOption): string | number | TOption {
    const { getItemId } = this.props;
    return getItemId ? getItemId(item) : item;
  }

  onRemoveItem(e: SyntheticEvent, item: TOption): void {
    const { selectedValues } = this.state;
    const { onChange } = this.props;

    this.setState(
      {
        selectedValues: selectedValues.filter((i) => this.getItemId(i) !== this.getItemId(item)),
      },
      () => {
        if (onChange) onChange(e, this.state.selectedValues);
      }
    );
  }

  onRemoveAllItem(e: SyntheticEvent): void {
    const { onChange } = this.props;
    this.setState({ selectedValues: [] }, () => {
      if (onChange) onChange(e, this.state.selectedValues);
    });
  }

  compareItems(a: TOption | null, b: TOption | null): boolean {
    if (a === null || b === null) return false;
    return this.getItemId(a) === this.getItemId(b);
  }

  filter(items: TOption[]): TOption[] {
    const { itemFilter } = this.props;
    const { selectedValues } = this.state;
    return items.filter(
      (o) =>
        o === null ||
        (selectedValues.find((s) => this.compareItems(s, o)) === undefined &&
          (!itemFilter || (itemFilter && itemFilter(o))))
    );
  }

  render(): React.ReactNode {
    const {
      items,
      label,
      sx,
      helperText,
      placeHolder,
      validationText,
      itemFilter,
      renderSelector,
      hasNoneItem,
      noneItemLabel,
      hasAllItem,
      allItemLabel,
      hasNewItem,
      disabled,
      className,
      disableCallback,
      isLoading,
    } = this.props;
    const { selectedValues, valid, allSelected } = this.state;

    const hasSelected = selectedValues && selectedValues.length ? 'has-selected' : '';

    return (
      <Stack
        className={
          'klayo-multiselector' +
          (hasNewItem ? ' klayo-multiselector--withnewitemoption' : '') +
          (className ? ' ' + className : '')
        }
        direction='column'
        ref={this.selectorContainerRef}
        gap={2}
      >
        {renderSelector ? (
          renderSelector({
            onChange: this.onChange.bind(this),
            onBlur: this.onBlur.bind(this),
            clearable: false,
            clearOnSelection: true,
            excludeItems: selectedValues,
            hasNoneItem: hasNoneItem,
            noneItemLabel: noneItemLabel,
            hasAllItem: hasAllItem,
            allItemLabel: allItemLabel,
            disabled: disabled,
            translate: this.props.translate,
          })
        ) : (
          <Autocomplete<TOption, true, true, false>
            className='klayo-selector'
            options={
              // While loading, expose no options so the dropdown shows the loading spinner
              // (loadingText) instead of stale/cached entries that may no longer be valid.
              isLoading
                ? []
                : items
                  ? itemFilter
                    ? this.filter(items.filter((i) => (i ? itemFilter(i) : true)))
                    : this.filter(items)
                  : []
            }
            value={selectedValues}
            inputValue={this.state.inputValue}
            getOptionLabel={(option) => this.getItemLabel(option)}
            onChange={(event, value, reason) => this.onChange(event, value, reason)}
            onInputChange={(event, value, reason) => this.onInputChange(event, value, reason)}
            {...(!isLoading
              ? {
                  filterOptions: (options, params) => {
                    const filtered = this.filterOptions(options, params);

                    if (hasAllItem)
                      filtered.concat({ id: -11, name: allItemLabel, isAllItem: true } as TOption);

                    const showNewItemOption =
                      !(
                        filtered.length === 1 &&
                        this.getItemLabel(filtered[0]).toUpperCase() ===
                          params.inputValue.trim().toUpperCase()
                      ) && hasNewItem;
                    return showNewItemOption
                      ? [...filtered, { id: -12, name: allItemLabel, isNewItem: true } as TOption]
                      : filtered;
                  },
                }
              : {
                  filterOptions: (options) => options,
                })}
            autoComplete
            includeInputInList
            filterSelectedOptions
            popupIcon={<DropIcon />}
            clearIcon={<RemoveIcon />}
            disableClearable={true}
            handleHomeEndKeys
            onBlur={this.onBlur.bind(this)}
            disabled={disabled}
            componentsProps={{
              paper: {
                className:
                  'klayo-multiselector_dropdownlist_paper' +
                  (hasNewItem ? ' klayo-multiselector_dropdownlist_paper--withnewitemoption' : ''),
              },
            }}
            ListboxProps={{
              className:
                'klayo-multiselector_dropdownlist' +
                (hasNewItem ? ' klayo-multiselector_dropdownlist--withnewitemoption' : ''),
            }}
            ChipProps={{
              deleteIcon: <RemoveIcon />,
            }}
            multiple
            renderOption={(props, option, state) =>
              this.renderOption(props, option, state as AutocompleteRenderOptionState)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={
                  placeHolder ? placeHolder : this.props.translate?.('common.search') || 'Search'
                }
                error={valid === false}
                helperText={valid === false ? validationText : helperText}
                sx={{ width: '100%' }}
                endAdornment={params.InputProps.endAdornment}
              />
            )}
            sx={sx}
            loading={isLoading}
            loadingText={
              <Box display='flex' justifyContent={'center'} alignItems={'center'}>
                <CircularProgress size={20} />
              </Box>
            }
            noOptionsText={this.props.translate?.('common.noOptions') || 'No options'}
            renderTags={(tagValue, getTagProps) => {
              return tagValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                const optionWithTitle = option as TOption & { title?: string };
                return (
                  <Chip key={key} label={optionWithTitle.title ?? ''} {...tagProps} disabled />
                );
              });
            }}
          />
        )}
        <Stack
          className={`klaro-chiparray ${hasSelected}`}
          direction='row'
          gap={1}
          sx={{ ...sx, flexWrap: 'wrap' }}
        >
          {hasAllItem && allSelected ? (
            <Chip
              key={0}
              label={allItemLabel}
              onDelete={(e) => this.onRemoveAllItem(e)}
              disabled={disabled}
              deleteIcon={<RemoveIcon />}
            />
          ) : (
            selectedValues.map((item, index) => {
              const itemWithValid = item as TOption & { isValid?: boolean };
              const itemLabel = (
                <div>
                  {this.getItemLabel(item)}{' '}
                  <IntegrationAbbreviation
                    data={item}
                    type={IntegrationAbbreviation.dataType.integrationUser.value}
                  />
                </div>
              );
              const disableItem = disableCallback ? disableCallback(item) : false;

              return disabled || disableItem ? (
                <Chip
                  key={index}
                  label={itemLabel}
                  {...(!(itemWithValid.isValid ?? true) && {
                    className: 'chip-error',
                  })}
                />
              ) : (
                <Chip
                  key={index}
                  label={itemLabel}
                  disabled={disabled || disableItem}
                  onDelete={(e) => this.onRemoveItem(e, item)}
                  deleteIcon={<RemoveIcon />}
                  {...(!(itemWithValid.isValid ?? true) && {
                    className: 'chip-error',
                  })}
                />
              );
            })
          )}
        </Stack>
      </Stack>
    );
  }
}
